
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { ChevronLeft, ChevronRight, Menu, X } from 'lucide-react';
import { ApplicationNav } from '@/components/application/ApplicationNav';
import { cn } from '@/lib/utils';
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet';
import type { Application } from '@/types/database.types';

interface CollapsibleSidebarProps {
  currentSection: number;
  onSectionChange: (index: number) => void;
  data: Partial<Application>;
  className?: string;
}

export const CollapsibleSidebar = ({
  currentSection,
  onSectionChange,
  data,
  className
}: CollapsibleSidebarProps) => {
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [isMobileOpen, setIsMobileOpen] = useState(false);

  // Mobile sidebar content
  const SidebarContent = () => (
    <div className="h-full py-4">
      <ApplicationNav
        currentSection={currentSection}
        onSectionChange={(index) => {
          onSectionChange(index);
          setIsMobileOpen(false); // Close mobile sidebar when section is selected
        }}
        data={data}
      />
    </div>
  );

  return (
    <>
      {/* Mobile Sheet Sidebar (visible on screens < lg) */}
      <div className="lg:hidden">
        <Sheet open={isMobileOpen} onOpenChange={setIsMobileOpen}>
          <SheetTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className="fixed top-4 left-4 z-50 rounded-full border bg-background/95 backdrop-blur-sm shadow-lg hover:bg-muted"
              aria-label="Open navigation menu"
            >
              <Menu className="h-5 w-5" />
            </Button>
          </SheetTrigger>
          <SheetContent 
            side="left" 
            className="w-80 p-0 border-r"
            aria-describedby="mobile-sidebar-description"
          >
            <div className="p-6 border-b bg-muted/30">
              <h2 className="text-lg font-semibold">Application Sections</h2>
            </div>
            <div id="mobile-sidebar-description" className="sr-only">
              Navigation menu with application sections
            </div>
            <div className="px-2">
              <SidebarContent />
            </div>
          </SheetContent>
        </Sheet>
      </div>

      {/* Desktop Collapsible Sidebar (visible on screens >= lg) */}
      <div className={cn(
        'hidden lg:block relative transition-all duration-300 border-r border-border bg-background', 
        isCollapsed ? 'w-16' : 'w-72',
        className
      )}>
        <div className={cn(
          'h-full transition-all duration-300 py-4',
          isCollapsed ? 'opacity-0 invisible' : 'opacity-100 visible'
        )}>
          <ApplicationNav
            currentSection={currentSection}
            onSectionChange={onSectionChange}
            data={data}
          />
        </div>
        
        <Button
          variant="ghost"
          size="icon"
          className="absolute -right-3 top-6 rounded-full border bg-background shadow-sm hover:bg-muted z-10"
          onClick={() => setIsCollapsed(!isCollapsed)}
          aria-label={isCollapsed ? "Expand sidebar" : "Collapse sidebar"}
        >
          {isCollapsed ? 
            <ChevronRight className="h-4 w-4" /> : 
            <ChevronLeft className="h-4 w-4" />}
        </Button>
        
        {isCollapsed && (
          <div className="flex flex-col items-center space-y-3 py-4 px-2">
            {Array.from({ length: 8 }).map((_, index) => (
              <Button
                key={index}
                variant={currentSection === index ? "default" : "ghost"}
                size="icon"
                className="rounded-full w-10 h-10 text-xs font-medium"
                onClick={() => onSectionChange(index)}
                aria-label={`Go to section ${index + 1}`}
              >
                {index + 1}
              </Button>
            ))}
          </div>
        )}
      </div>
    </>
  );
};
